import { MapPin, Camera, Quote, FileText, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ResearchHit } from '../../types/graphql';

interface SourceCardProps {
  hit: ResearchHit;
  anchorId?: string;
  lang?: string;
}

/** Jump-to-source links: the atomized topic in My Content, or the original URL. */
function SourceActions({ hit }: { hit: ResearchHit }) {
  const hasOpen = hit.type === 'fact' && hit.topicId;
  if (!hasOpen && !hit.url) return null;
  return (
    <div className="flex flex-wrap gap-3 mt-2 text-xs">
      {hasOpen && (
        <Link
          to={`/content/${hit.topicId}`}
          className="inline-flex items-center gap-1 text-cmp-ink-2 hover:text-primary transition-colors"
        >
          <FileText className="w-3 h-3" />
          Open article
        </Link>
      )}
      {hit.url && (
        <a
          href={hit.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-cmp-ink-2 hover:text-primary transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          View source
        </a>
      )}
    </div>
  );
}

export function SourceCard({ hit, anchorId, lang = 'en' }: SourceCardProps) {
  const isPhoto = hit.type === 'photo';

  const badge = hit.citationId ? (
    <span className="inline-block bg-primary text-onfill text-xs font-semibold px-1.5 py-0.5 rounded mr-2 align-middle">
      {hit.citationId}
    </span>
  ) : null;

  if (isPhoto) {
    return (
      <div
        id={anchorId}
        className="bg-form-dark rounded-lg p-4 border-l-2 border-primary flex gap-3"
      >
        {hit.thumbnailUrl ? (
          // eslint-disable-next-line jsx-a11y/img-redundant-alt
          <img
            src={hit.thumbnailUrl}
            alt={hit.caption || 'Archive photo'}
            className="w-24 min-h-20 rounded object-cover bg-core-dark flex-shrink-0"
          />
        ) : (
          <div className="w-24 min-h-20 rounded bg-core-dark flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-cmp-ink">
            {badge}
            {hit.articleTitle || hit.headline || 'Untitled'}
          </div>
          {hit.photoDate && <div className="text-xs text-cmp-ink-2 mt-0.5">{hit.photoDate}</div>}
          {hit.caption && <p className="text-sm text-cmp-ink mt-2">{hit.caption}</p>}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-cmp-ink-2">
            {hit.photoLocation && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {hit.photoLocation}
              </span>
            )}
            {hit.credit && (
              <span className="inline-flex items-center gap-1">
                <Camera className="w-3 h-3" />
                {hit.credit}
              </span>
            )}
          </div>
          <SourceActions hit={hit} />
        </div>
      </div>
    );
  }

  const factText = lang === 'ar' && hit.factTextAr ? hit.factTextAr : hit.factText;

  return (
    <div id={anchorId} className="bg-form-dark rounded-lg p-4 border-l-2 border-primary">
      <div className="text-sm font-semibold text-cmp-ink">
        {badge}
        {hit.articleTitle || 'Untitled'}
      </div>
      {(hit.readableDate || hit.eventDate) && (
        <div className="text-xs text-cmp-ink-2 mt-0.5">{hit.readableDate || hit.eventDate}</div>
      )}
      {factText && <p className="text-sm text-cmp-ink mt-2">{factText}</p>}
      {hit.sourceQuote && (
        <div className="mt-2 text-xs text-cmp-ink-2 italic border-l-2 border-cmp-line-2 pl-2 flex gap-1.5">
          <Quote className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span>{hit.sourceQuote}</span>
        </div>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-cmp-ink-2">
        {hit.attribution && <span>Attribution: {hit.attribution}</span>}
        {hit.sourceType && <span>Source: {hit.sourceType.replace(/_/g, ' ')}</span>}
      </div>
      <SourceActions hit={hit} />
    </div>
  );
}
